import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { TraceSpan, TransactionDetail } from 'src/app/models/tracing.model';
import { TracingService } from 'src/app/services/tracing.service';

interface WaterfallRow {
  span: TraceSpan;
  offsetPercent: number;
  widthPercent: number;
}

@Component({
  selector: 'app-project-trace-detail',
  templateUrl: './project-trace-detail.component.html',
  styleUrls: ['./project-trace-detail.component.scss'],
  standalone: false
})
export class ProjectTraceDetailPageComponent implements OnInit {

  projectId: string = null;
  transactionId: string = null;

  transaction: TransactionDetail | null = null;
  loading = true;
  error = false;

  showAllSpans = false;

  readonly visibleSpanLimit = 60;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tracingService: TracingService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid');
    this.transactionId = this.route.snapshot.paramMap.get('transactionUuid');

    try {
      this.transaction = await lastValueFrom(
        this.tracingService.getTransaction(this.projectId, this.transactionId)
      );
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  // -- waterfall ----------------------------------------------------------

  /** Function spans are per-request totals, not points on the timeline, so the
   * waterfall shows only spans that really happened at a moment. */
  get timelineSpans(): TraceSpan[] {
    return (this.transaction?.spans ?? []).filter(span => span.op !== 'function');
  }

  get waterfall(): WaterfallRow[] {
    const total = this.transaction?.duration_ms || 1;
    const spans = this.showAllSpans
      ? this.timelineSpans
      : this.timelineSpans.slice(0, this.visibleSpanLimit);

    return spans.map(span => ({
      span,
      offsetPercent: Math.min((span.start_offset_ms / total) * 100, 100),
      widthPercent: Math.max((span.duration_ms / total) * 100, 0.4),
    }));
  }

  get hiddenSpanCount(): number {
    return Math.max(this.timelineSpans.length - this.waterfall.length, 0);
  }

  get maxFunctionDuration(): number {
    return Math.max(...(this.transaction?.function_spans ?? []).map(f => f.duration_ms), 1);
  }

  functionShare(duration: number): number {
    return Math.max((duration / this.maxFunctionDuration) * 100, 2);
  }

  spanClass(op: string): string {
    if (op.startsWith('db')) return 'span--db';
    if (op.startsWith('http')) return 'span--http';
    if (op.startsWith('cache')) return 'span--cache';
    if (op.startsWith('template')) return 'span--template';
    return 'span--custom';
  }

  // -- context ------------------------------------------------------------

  get userEntries(): { key: string; value: string }[] {
    return Object.entries(this.transaction?.user_context ?? {}).map(([key, value]) => ({ key, value }));
  }

  get tagEntries(): { key: string; value: string }[] {
    return Object.entries(this.transaction?.tags ?? {}).map(([key, value]) => ({ key, value }));
  }

  get headerEntries(): { key: string; value: string }[] {
    return Object.entries(this.transaction?.request?.headers ?? {}).map(([key, value]) => ({ key, value }));
  }

  get databaseShare(): number {
    if (!this.transaction?.duration_ms) return 0;
    return (this.transaction.db_time_ms / this.transaction.duration_ms) * 100;
  }

  // -- formatting ---------------------------------------------------------

  formatDuration(ms: number): string {
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
    if (ms >= 1) return `${ms.toFixed(1)} ms`;
    return `${ms.toFixed(2)} ms`;
  }

  formatDateTime(value: string): string {
    return new Date(value).toLocaleString();
  }

  goBack() {
    this.router.navigate(['/main', 'project', this.projectId, 'tracing']);
  }
}
